<script>
import firebase from "firebase/app";
import "firebase/firestore";
import Swal from "sweetalert2";

import Portal from "../../layouts/portal";
import { clienteComputed } from "@/state/helpers";

/**
 * Completar registro, como en la app: primero se elige "sin negocio" o "con
 * negocio", y según la elección se completa el perfil o se registra el negocio.
 *
 * - Sin negocio: se piden nombre y teléfono y el perfil queda completo
 *   (`status: signup_completed`, `type: normal`).
 * - Con negocio: se manda a "Mi empresa", que registra el negocio y activa al
 *   usuario como comercio.
 */
export default {
  page: { title: "Completa tu registro" },
  components: { Portal },
  data() {
    return {
      paso: "elige", // "elige" | "sin-negocio"
      name: "",
      phone: "",
      referido: "",
      enviado: false,
      guardando: false,
      error: null,
    };
  },
  computed: {
    ...clienteComputed,
    phoneValido() {
      return /^\d{10}$/.test(this.phone.trim());
    },
  },
  methods: {
    elegirSinNegocio() {
      const p = this.perfilCliente || {};
      // El nombre puede venir del correo (así lo deja el alta); no se
      // precarga si es igual al correo, para que escriban su nombre real.
      this.name = p.name && p.name !== p.email ? p.name : "";
      this.phone = p.phone ? String(p.phone).replace(/^\+52/, "") : "";
      this.paso = "sin-negocio";
      this.error = null;
    },

    elegirConNegocio() {
      this.$router.push({ name: "portal-empresa" });
    },

    async completar() {
      this.enviado = true;
      if (!this.name.trim() || !this.phoneValido) {
        this.error = "Escribe tu nombre y un teléfono de 10 dígitos.";
        return;
      }
      const id = this.perfilCliente && this.perfilCliente.id;
      if (!id) {
        this.error = "No pudimos identificar tu cuenta.";
        return;
      }

      this.guardando = true;
      this.error = null;
      try {
        const datos = {
          name: this.name.trim(),
          phone: `+52${this.phone.trim()}`,
          type: "normal",
          status: "signup_completed",
        };
        if (this.referido.trim()) datos.referred_by = this.referido.trim().toUpperCase();

        await firebase.firestore().collection("app_usuarios").doc(id).update(datos);
        await this.$store.dispatch("cliente/cargar", { force: true });

        Swal.fire("¡Listo!", "Tu registro quedó completo.", "success");
        // Ya con el perfil completo entra al portal por Home.
        this.$router.push({ name: "portal" });
      } catch (err) {
        console.error("[portal] error al completar el registro:", err);
        this.error = "No pudimos guardar tus datos. Inténtalo de nuevo.";
      } finally {
        this.guardando = false;
      }
    },
  },
  mounted() {
    // Si el perfil ya está completo, no hay nada que completar.
    if (this.perfilCliente && !this.perfilIncompleto) {
      this.$router.replace({ name: "portal-cuenta" });
    }
  },
};
</script>

<template>
  <Portal>
    <div class="completar">
      <h1 class="h3 text-center mb-4">Completa tu registro</h1>

      <!-- Elección -->
      <div v-if="paso === 'elige'">
        <div class="hero mb-4">
          <img :src="require('@/assets/images/recnet-header.png')" alt="Recnet" />
          <div class="hero-texto">¡Elige tu tipo de registro!</div>
        </div>

        <h2 class="h5 text-center mb-4">¿Cómo quieres unirte a RecNet?</h2>

        <b-row class="justify-content-center">
          <b-col cols="6" md="4">
            <button type="button" class="opcion" @click="elegirSinNegocio">
              <i class="mdi mdi-account-outline"></i>
              <span>Registrarme sin negocio</span>
            </button>
          </b-col>
          <b-col cols="6" md="4">
            <button type="button" class="opcion" @click="elegirConNegocio">
              <i class="mdi mdi-storefront-outline"></i>
              <span>Registra tu negocio</span>
            </button>
          </b-col>
        </b-row>
      </div>

      <!-- Completar sin negocio -->
      <div v-else class="completar-card">
        <b-button variant="link" class="px-0 mb-2" @click="paso = 'elige'">
          <i class="mdi mdi-arrow-left"></i> Volver
        </b-button>

        <h2 class="h5 mb-3">Tus datos</h2>

        <b-alert :show="!!error" variant="danger" class="py-2 small">{{ error }}</b-alert>

        <b-form @submit.prevent="completar">
          <b-form-group label="Nombre completo">
            <b-form-input v-model="name" placeholder="Tu nombre"
              :class="{ 'is-invalid': enviado && !name.trim() }" />
          </b-form-group>

          <b-form-group label="Teléfono (10 dígitos)">
            <b-input-group prepend="+52">
              <b-form-input v-model="phone" type="tel" maxlength="10" placeholder="6141234567"
                :class="{ 'is-invalid': enviado && !phoneValido }" />
            </b-input-group>
          </b-form-group>

          <b-form-group label="¿Te invitó alguien? (opcional)">
            <b-form-input v-model="referido" placeholder="Código de referido" />
          </b-form-group>

          <b-button type="submit" variant="primary" block :disabled="guardando">
            <b-spinner v-if="guardando" small class="mr-2" />
            {{ guardando ? 'Guardando…' : 'Completar mi registro' }}
          </b-button>
        </b-form>
      </div>
    </div>
  </Portal>
</template>

<style scoped>
.completar {
  max-width: 720px;
  margin: 0 auto;
}

.hero {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.hero img {
  width: 100%;
  height: 220px;
  object-fit: cover;
  display: block;
}

.hero-texto {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.8rem;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  background: rgba(75, 46, 131, 0.25);
}

.opcion {
  width: 100%;
  background: #eef3fb;
  border: 1px solid #d6e0f0;
  border-radius: 12px;
  padding: 28px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #4b2e83;
  font-weight: 600;
  text-align: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.opcion:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(75, 46, 131, 0.15);
}

.opcion i {
  font-size: 40px;
}

.completar-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  max-width: 460px;
  margin: 0 auto;
}
</style>
